import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import NumberFormat from 'react-number-format';
import { BackdropCircular } from 'components';
import {
  Card,
  Button,
  Divider,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import {getSubscriptionBenefitsApi} from 'api/subscription';
import { changeMarketingData } from 'redux/actions/subscription';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { calculateAddonsMonthlyPrice, calculateAddonsOneTypePrice, calculateMonthlySubscriptionByPeriod } from 'helpers/priceCalculations';


const useStyles = makeStyles(() => ({
  price: {
    color: '#0A468C',
    fontSize: '29.6px',
    fontWeight: 600,
  },
  buttonContainer: {
    position: 'relative',
    height: '55px'
  },
  payTypeButton: {
    position: 'absolute',
    width: 180,
    height: '55px',
    borderRadius: '25px'
  },
  annuallyButton: {
    background: '#E0E0E0',
    border: '0px solid #fff',
    zIndex: 0,
    '&:hover': {
      border: '1px solid #103A99',
      background: '#ffff',
      zIndex: 4,
    }
  },
  semiAnualiButton: {
    background: '#EEEEEE',
    border: '0px solid #fff',
    left: '30%',
    zIndex: 1,
    '&:hover': {
      border: '1px solid #103A99',
      background: '#ffff',
      zIndex: 4,
    }
  },
  monthlyButton: {
    background: '#E0E0E0',
    border: '0px solid #fff',
    left: '60%',
    zIndex: 0,
    '&:hover': {
      border: '1px solid #103A99',
      background: '#ffff',
      zIndex: 4,
    }
  },
  activeButton: {
    border: '1px solid #103A99',
    background: '#ffff',
    zIndex: 3,
  }
}));

const ListBullet = withStyles({
  root: {
    padding: '3px 0',

    '& .MuiListItem-root': {
      position: 'relative',
      padding: '3px 5px 3px 16px'
    },

    '& .MuiListItem-root::before': {
      content: '" "',
      display: 'inline-block',
      position: 'absolute',
      left: '6px',
      width: '4px',
      height: '4px',
      backgroundColor: '#3C4257',
      borderRadius: '50%',
      marginRight: '10px'
    }
  }
})(List);

const CheckIcon = withStyles({
  root: {
    minWidth: '28px',
    color: '#0A468C',
  }
})(ListItemIcon);

const ListFeatures = withStyles({
  root: {
    padding: '3px 0',

    '& .MuiListItem-root': {
      padding: '3px 5px 3px 5px'
    }
  }
})(List);

const periodTitle = {
  annually: 'Annual',
  semiAnnually: 'Semi-Annual',
  monthly: 'monthly',
};


export default () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    userProfile: {onboarding, marketing},
    additionalSkus,
    additionalSpecials,
    additionalFeatures,
    subscriptionBenefits,
    isLoadingAdditionalSkus,
    isLoadingAdditionalSpecials,
    isLoadingAdditionalFeatures,
    isLoadingMarketignData,
    isLoadingMarketingBilling,
    isLoadingSubscriptionBenefits,
  } = useSelector((state: IReduxState) => state.subscription);
  const {featureIds, skuIds, specialIds, plan, selectedBenefit} = marketing;
  const enabledSubscriptionBenefits = subscriptionBenefits[plan]?.filter((o) => o.isEnabled);

  useEffect(() => {
    getSubscriptionBenefitsApi({ dispatch });
  }, []);


  const handleChangePerion = (newPerion) => {
    dispatch(changeMarketingData({ ...marketing, plan: newPerion, selectedBenefit: null  }));
  };

  const onboardingOneTimePrice = Number(onboarding?.totalOneTimePrice) || 0;
  const baseMonthlyPrice = Number(onboarding?.totalMonthlyPrice) || 0;

  const selectedSku = additionalSkus.find((opt) => opt._id === skuIds[0]);

  const loading = isLoadingAdditionalSkus ||
    isLoadingAdditionalSpecials ||
    isLoadingAdditionalFeatures ||
    isLoadingMarketignData ||
    isLoadingMarketingBilling ||
    isLoadingSubscriptionBenefits;

  const {
    value: skusCount,
    price: skusPrice,
    isOneTime: skusIsOneTime
  } = selectedSku || {};

  const selectedSpecial = additionalSpecials.find((opt) => opt._id === specialIds[0]);
  const {
    value: specialsCount,
    price: specialsPrice,
    isOneTime: specialsIsOneTime
  } = selectedSpecial || {};

  const isOneTimeFeaturesHavePrice = featureIds
    .filter((featureId) => {
      const {priceOneTime} = additionalFeatures.find((opt) => opt._id === featureId) || {};
      return priceOneTime != 0;
    }) || [];

  const isMonthlyFeaturesHavePrice = featureIds
    .filter((featureId) => {
      const {priceMonthly} = additionalFeatures.find((opt) => opt._id === featureId) || {};
      return priceMonthly != 0;
    }) || [];

  const addonsMonthlyPrice = calculateAddonsMonthlyPrice({
    featureIds,
    additionalFeatures,
    skusPrice,
    skusIsOneTime,
    specialsPrice,
    specialsIsOneTime
  });
  const addonsOneTypePrice = calculateAddonsOneTypePrice({
    featureIds,
    additionalFeatures
  });

  return (
    <>
      <Card elevation={25} className="min-w-600 max-w-650 p-8 mx-4">
        <Typography variant="h5" color="primary">SUMMARY</Typography>
        <ListBullet className="ml-2">
          <ListItem className="flex items-center justify-between w-full text-dark-gray">
            <Typography variant="h5" className="h5 mb-0 font-light">Base Store Setup Fee:</Typography>
            <NumberFormat
              className="h5 font-semibold"
              value={onboardingOneTimePrice}
              displayType="text"
              thousandSeparator={','}
              decimalSeparator={'.'}
              prefix={'$'}
              fixedDecimalScale
              decimalScale={2}
            />
          </ListItem>
        </ListBullet>
        <ListBullet className="ml-2">
          <ListItem className="flex items-center justify-between w-full text-dark-gray">
            <Typography variant="h5" className="h5 mb-0 font-light">Base Store Subscription:</Typography>
            <NumberFormat
              className="h5 font-semibold"
              value={baseMonthlyPrice}
              displayType="text"
              thousandSeparator={','}
              decimalSeparator={'.'}
              prefix={'$'}
              fixedDecimalScale
              decimalScale={2}
            />
          </ListItem>
        </ListBullet>
        <Divider className="mt-2" />
        {(!!isOneTimeFeaturesHavePrice.length || skusIsOneTime || specialsIsOneTime) &&
        <>
          <Typography variant="h5" className="h5 mt-5">Features/Add-Ons Setup Fee</Typography>
          <ListFeatures className="ml-2 mb-5">
            {featureIds.map((featureId) => {
              const additionalFeature = additionalFeatures.find((opt) => opt._id === featureId);
              const {label, priceOneTime} = additionalFeature || {};

              if (priceOneTime == 0) {
                return null;
              }

              return (
                <ListItem key={featureId} className="flex items-center justify-between w-full text-dark-gray">
                  <CheckIcon>
                    <Icon fontSize="small">check</Icon>
                  </CheckIcon>
                  <div className="flex items-center justify-between w-full">
                    <span>{label}</span>
                    <NumberFormat
                      className="h5 font-semibold"
                      value={priceOneTime}
                      displayType="text"
                      thousandSeparator={','}
                      decimalSeparator={'.'}
                      prefix={'$'}
                      fixedDecimalScale
                      decimalScale={2}
                    />
                  </div>
                </ListItem>
              );
            })}
            {(!!skusCount && skusIsOneTime) &&
            <ListItem className="flex items-center justify-between w-full text-dark-gray">
              <CheckIcon>
                <Icon fontSize="small">check</Icon>
              </CheckIcon>
              <div className="flex items-center justify-between w-full">
                <span>
                  Additional SKUs:{' '}
                  <strong>Store {skusCount} SKUs</strong>
                </span>
                <NumberFormat
                  className="h5 font-semibold"
                  value={skusPrice}
                  displayType="text"
                  thousandSeparator={','}
                  decimalSeparator={'.'}
                  prefix={'$'}
                  fixedDecimalScale
                  decimalScale={2}
                />
              </div>
            </ListItem>
            }
            {(!!specialsCount && specialsIsOneTime) &&
            <ListItem className="flex items-center justify-between w-full text-dark-gray">
              <CheckIcon>
                <Icon fontSize="small">check</Icon>
              </CheckIcon>
              <div className="flex items-center justify-between w-full">
                <span>
                  Specials:{' '}
                  <strong>{specialsCount} Specials - concurrent specials per month</strong>
                </span>
                <NumberFormat
                  className="h5 font-semibold"
                  value={specialsPrice}
                  displayType="text"
                  thousandSeparator={','}
                  decimalSeparator={'.'}
                  prefix={'$'}
                  fixedDecimalScale
                  decimalScale={2}
                />
              </div>
            </ListItem>
            }
          </ListFeatures>
          <Divider className="mt-2" />
        </>
        }
        {(!!isMonthlyFeaturesHavePrice.length || !skusIsOneTime || !specialsIsOneTime) &&
        <>
          <Typography variant="h5" className="h5 mt-5">Features/Add-Ons Monthly Cost</Typography>
          <ListFeatures className="ml-2">
            {featureIds.map((featureId) => {
              const additionalFeature = additionalFeatures.find((opt) => opt._id === featureId);
              const {label, priceMonthly} = additionalFeature || {};

              if (!additionalFeature || priceMonthly == 0) {
                return null;
              }

              return (
                <ListItem key={featureId} className="flex items-center justify-between w-full text-dark-gray">
                  <CheckIcon>
                    <Icon fontSize="small">check</Icon>
                  </CheckIcon>
                  <div className="flex items-center justify-between w-full">
                    <span>{label}</span>
                    <NumberFormat
                      className="h5 font-semibold"
                      value={priceMonthly}
                      displayType="text"
                      thousandSeparator={','}
                      decimalSeparator={'.'}
                      prefix={'$'}
                      fixedDecimalScale
                      decimalScale={2}
                    />
                  </div>
                </ListItem>
              );
            })}
            {(!!skusCount && !skusIsOneTime) &&
            <ListItem className="flex items-center justify-between w-full text-dark-gray">
              <CheckIcon>
                <Icon fontSize="small">check</Icon>
              </CheckIcon>
              <div className="flex items-center justify-between w-full">
                <span>
                  Additional SKUs:{' '}
                  <strong>Store {skusCount} SKUs</strong>
                </span>
                <NumberFormat
                  className="h5 font-semibold"
                  value={skusPrice}
                  displayType="text"
                  thousandSeparator={','}
                  decimalSeparator={'.'}
                  prefix={'$'}
                  fixedDecimalScale
                  decimalScale={2}
                />
              </div>
            </ListItem>
            }
            {(!!specialsCount && !specialsIsOneTime) &&
            <ListItem className="flex items-center justify-between w-full text-dark-gray">
              <CheckIcon>
                <Icon fontSize="small">check</Icon>
              </CheckIcon>
              <div className="flex items-center justify-between w-full">
                <span>
                  Specials:{' '}
                  <strong>{specialsCount} Specials - concurrent specials per month</strong>
                </span>
                <NumberFormat
                  className="h5 font-semibold"
                  value={specialsPrice}
                  displayType="text"
                  thousandSeparator={','}
                  decimalSeparator={'.'}
                  prefix={'$'}
                  fixedDecimalScale
                  decimalScale={2}
                />
              </div>
            </ListItem>
            }
          </ListFeatures>
          <Divider className="mt-2" />
        </>
        }
        <div className="mt-5">
          <NumberFormat
            className={classes.price}
            value={addonsOneTypePrice + onboardingOneTimePrice}
            displayType="text"
            thousandSeparator={','}
            decimalSeparator={'.'}
            prefix={'$'}
            fixedDecimalScale
            decimalScale={2}
          />
          <span className="ml-2 font-semibold text-dark-gray">/ SETUP FEE</span>
        </div>
        <div className="mt-5">
          <NumberFormat
            className={classes.price}
            value={calculateMonthlySubscriptionByPeriod({addonsMonthlyPrice, baseMonthlyPrice, plan})}
            displayType="text"
            thousandSeparator={','}
            decimalSeparator={'.'}
            prefix={'$'}
            fixedDecimalScale
            decimalScale={2}
          />
          <span className="ml-2 font-semibold text-dark-gray">/ MONTH</span>
          <Typography variant="h5" className="h5 mt-2">
            {plan === 'annually' &&
              <NumberFormat
                className="h5 font-semibold"
                value={calculateMonthlySubscriptionByPeriod({addonsMonthlyPrice, baseMonthlyPrice, plan}) * 12}
                displayType="text"
                thousandSeparator={','}
                decimalSeparator={'.'}
                prefix={'billed at $'}
                suffix={'/yr'}
                fixedDecimalScale
                decimalScale={2}
              />}
            {plan === 'semiAnnually' &&
              <NumberFormat
                className="h5 font-semibold"
                value={calculateMonthlySubscriptionByPeriod({addonsMonthlyPrice, baseMonthlyPrice, plan}) * 6}
                displayType="text"
                thousandSeparator={','}
                decimalSeparator={'.'}
                prefix={'billed at $'}
                suffix={' every 6 month'}
                fixedDecimalScale
                decimalScale={2}
              />}
            {plan === 'monthly' &&
              <NumberFormat
                className="h5 font-semibold"
                value={calculateMonthlySubscriptionByPeriod({addonsMonthlyPrice, baseMonthlyPrice, plan})}
                displayType="text"
                thousandSeparator={','}
                decimalSeparator={'.'}
                prefix={'billed monthly $'}
                fixedDecimalScale
                decimalScale={2}
              />}
          </Typography>
          <div className={`mt-5 ${classes.buttonContainer}`}>
            <Button
              className={`
              ${classes.payTypeButton}
              ${classes.annuallyButton}
              ${plan == 'annually' ? classes.activeButton : ''}
            `}
              color="primary"
              variant="outlined"
              onClick={() => handleChangePerion('annually')}
            >
              <div className="font-semibold text-dark-gray">Pay Annually</div>
            </Button>
            <Button
              className={`
              ${classes.payTypeButton}
              ${classes.semiAnualiButton}
              ${plan == 'semiAnnually' ? classes.activeButton : ''}
            `}
              color="primary"
              variant="outlined"
              onClick={() => handleChangePerion('semiAnnually')}
            >
              <div className="flex-column">
                <div className="font-semibold text-dark-gray">Pay Semi-Annually</div>
                <div className="font-semibold">Plus 7%</div>
              </div>
            </Button>
            <Button
              className={`
              ${classes.payTypeButton} 
              ${classes.monthlyButton} 
              ${plan == 'monthly' ? classes.activeButton : ''}
            `}
              color="primary"
              variant="outlined"
              onClick={() => handleChangePerion('monthly')}
            >
              <div className="flex-column">
                <div className="font-semibold text-dark-gray">Pay Monthly</div>
                <div className="font-semibold">Plus 15%</div>
              </div>
            </Button>
          </div>
          {(plan == 'monthly' || plan == 'semiAnnually') &&
            <p className="font-bold">
              You have selected a {periodTitle[plan]} payment option. This is a 1-year contract.
            </p>}
        </div>
      </Card>

      {!!enabledSubscriptionBenefits?.length &&
      <Card elevation={25} className="h-fit max-w-500 p-8 mx-4">
        <Typography variant="h3" color="primary">Pick your FREE UPGRADE with your {periodTitle[plan]} Plan</Typography>
        <Divider className="mt-6 mb-6" />
        <FormControl className="w-full">
          <RadioGroup
            aria-label="subscriptionBenefits"
            name="subscriptionBenefits"
            value={selectedBenefit}
            onChange={(event) => dispatch(changeMarketingData({ ...marketing, selectedBenefit: event.target.value }))}
          >
            {enabledSubscriptionBenefits.map((option) => (
              <FormControlLabel
                key={option._id}
                value={option.title}
                control={<Radio color="primary" size="medium" />}
                label={option.title}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Card>}

      <BackdropCircular loading={loading} />
    </>
  );
};
